import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Filter, Search, X } from 'lucide-react';
import { RoundedSelect } from '../components/RoundedSelect';
import {
  TableColumnFilter,
  buildColumnFilterOptions,
  normalizeColumnFilterValue,
} from '../components/TableColumnFilter';
import { FloatingNotices } from '../components/FloatingNotices';
import { fetchJsonCached } from '../utils/portalQueryCache';
import { useDebouncedValue } from '../utils/useDebouncedValue';

const LOGS_FETCH_SIZE = 100;

function getLogTime(row) {
  return formatUtcToLocal(row.dateTimeUtc || row.DateTimeUtc || row.dateTime || row.DateTime);
}

function getLogActor(row) {
  return row.actorEmail || row.ActorEmail || '';
}

function getLogAction(row) {
  return row.action || row.Action || '';
}

function getLogEntityType(row) {
  return row.entityType || row.EntityType || '';
}

function getLogEntityId(row) {
  return row.entityId || row.EntityId || '';
}

function passesColumnFilters(entry, columnFilters, optionsByKey) {
  for (const [key, selected] of Object.entries(columnFilters)) {
    if (!selected) continue;
    const options = optionsByKey[key];
    if (!options || options.length === 0) continue;
    if (selected.size === 0) return false;
    if (selected.size >= options.length) continue;
    const getter = COLUMN_FILTER_GETTERS[key];
    if (!getter) continue;
    const value = normalizeColumnFilterValue(getter(entry));
    if (!selected.has(value)) return false;
  }
  return true;
}

const COLUMN_FILTER_GETTERS = {
  time: getLogTime,
  actor: getLogActor,
  action: getLogAction,
  entity: getLogEntityType,
  entityId: getLogEntityId,
};

function tryPrettyJson(value) {
  const text = typeof value === 'string' ? value.trim() : '';
  if (!text) return '';
  try {
    const parsed = JSON.parse(text);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return text;
  }
}

const logTimeFmt = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

const logTimeFmtFull = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

function formatUtcToLocal(utc, { full = false } = {}) {
  if (!utc) return '';
  const d = new Date(utc);
  if (Number.isNaN(d.getTime())) return String(utc);
  return (full ? logTimeFmtFull : logTimeFmt).format(d);
}

export function LogsPage() {
  const [items, setItems] = useState([]);
  const [remoteTotal, setRemoteTotal] = useState(0);
  const [columnFilters, setColumnFilters] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [actorEmail, setActorEmail] = useState('');
  const [fromUtc, setFromUtc] = useState('');
  const [toUtc, setToUtc] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [details, setDetails] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filtersPos, setFiltersPos] = useState(null);
  const filtersButtonRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);

  const debouncedSearch = useDebouncedValue(search, 250);

  const fetchUrl = useMemo(() => {
    const qp = new URLSearchParams();
    if (debouncedSearch.trim()) qp.set('search', debouncedSearch.trim());
    if (action) qp.set('action', action);
    if (entityType) qp.set('entityType', entityType);
    if (actorEmail.trim()) qp.set('actorEmail', actorEmail.trim());
    if (fromUtc) qp.set('fromUtc', new Date(fromUtc).toISOString());
    if (toUtc) qp.set('toUtc', new Date(toUtc).toISOString());
    qp.set('page', '1');
    qp.set('pageSize', String(LOGS_FETCH_SIZE));
    return `/api/admin/logs?${qp.toString()}`;
  }, [debouncedSearch, action, entityType, actorEmail, fromUtc, toUtc]);

  const onColumnFilterChange = (key, selected) => {
    setColumnFilters((current) => {
      const next = { ...current };
      if (!selected) {
        delete next[key];
      } else {
        next[key] = selected;
      }
      return next;
    });
    setPage(1);
  };

  const fetchUrlRef = useRef(fetchUrl);
  fetchUrlRef.current = fetchUrl;

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    (async () => {
      setError('');
      setIsLoading(true);
      try {
        const data = await fetchJsonCached(fetchUrl, { ttlMs: 15_000, signal: controller.signal });
        if (cancelled) return;
        setItems(Array.isArray(data?.items) ? data.items : Array.isArray(data?.Items) ? data.Items : []);
        setRemoteTotal(Number(data?.total ?? data?.Total ?? 0));
      } catch (e) {
        if (cancelled) return;
        setError(e.message || 'Failed to load logs.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [fetchUrl]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, action, entityType, actorEmail, fromUtc, toUtc]);

  useEffect(() => {
    const poll = async () => {
      if (document.hidden) return;
      const u = fetchUrlRef.current;
      const controller = new AbortController();
      try {
        const data = await fetchJsonCached(u, { ttlMs: 0, signal: controller.signal });
        setItems(Array.isArray(data?.items) ? data.items : Array.isArray(data?.Items) ? data.Items : []);
        setRemoteTotal(Number(data?.total ?? data?.Total ?? 0));
      } catch {
        /* silent refresh */
      }
    };
    const id = window.setInterval(poll, 12_000);
    const onVis = () => {
      if (!document.hidden) void poll();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  useEffect(() => {
    const onColumnFilterOpened = () => {
      setFiltersOpen(false);
      setFiltersPos(null);
    };
    document.addEventListener('table-column-filter:open', onColumnFilterOpened);
    return () => document.removeEventListener('table-column-filter:open', onColumnFilterOpened);
  }, []);

  useEffect(() => {
    if (!filtersOpen) return undefined;
    const close = () => {
      setFiltersOpen(false);
      setFiltersPos(null);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [filtersOpen]);

  useEffect(() => {
    if (!error) return undefined;
    const handle = window.setTimeout(() => setError(''), 3500);
    return () => window.clearTimeout(handle);
  }, [error]);

  useEffect(() => {
    if (!info) return undefined;
    const handle = window.setTimeout(() => setInfo(''), 3500);
    return () => window.clearTimeout(handle);
  }, [info]);

  const columnFilterOptions = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(COLUMN_FILTER_GETTERS).map(([key, getValue]) => [
          key,
          buildColumnFilterOptions(items, getValue),
        ])
      ),
    [items]
  );

  const filteredItems = useMemo(
    () => items.filter((entry) => passesColumnFilters(entry, columnFilters, columnFilterOptions)),
    [items, columnFilters, columnFilterOptions]
  );

  const columnFiltersLimited = remoteTotal > items.length;
  const filteredCount = filteredItems.length;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(Math.max(0, filteredCount) / pageSize)),
    [filteredCount, pageSize]
  );

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  const pagedItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredItems.slice(start, start + pageSize);
  }, [filteredItems, page, pageSize]);

  const actionOptions = useMemo(() => {
    const uniq = new Set();
    items.forEach((it) => {
      if (it?.action) uniq.add(it.action);
      if (it?.Action) uniq.add(it.Action);
    });
    return Array.from(uniq).sort();
  }, [items]);

  const entityTypeOptions = useMemo(() => {
    const uniq = new Set();
    items.forEach((it) => {
      if (it?.entityType) uniq.add(it.entityType);
      if (it?.EntityType) uniq.add(it.EntityType);
    });
    return Array.from(uniq).sort();
  }, [items]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (action) count += 1;
    if (entityType) count += 1;
    if (actorEmail.trim()) count += 1;
    if (fromUtc) count += 1;
    if (toUtc) count += 1;
    return count;
  }, [action, entityType, actorEmail, fromUtc, toUtc]);

  return (
    <section className="admin-stack">
      <motion.article className="panel-card">
        <div className="panel-title">
          <div className="users-panel-title-left">
            <h2>Logs</h2>
          </div>
          <div className="users-panel-actions">
            <button
              ref={filtersButtonRef}
              type="button"
              className="ghost-button admin-inline-button compact"
              onClick={() => {
                const rect = filtersButtonRef.current?.getBoundingClientRect?.();
                if (!rect) {
                  setFiltersOpen((v) => !v);
                  return;
                }
                setFiltersPos({ top: rect.bottom + 10, left: Math.min(rect.left, window.innerWidth - 560) });
                setFiltersOpen((v) => !v);
              }}
            >
              <Filter size={14} />
              Filters{activeFiltersCount ? ` (${activeFiltersCount})` : ''}
            </button>
          </div>
        </div>

        <div className="admin-filters logs-compact-toolbar">
          <div className="search-input-wrap">
            <Search size={14} />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search action, actor, entity, JSON..." />
          </div>
          <div className="logs-loading-pill" aria-live="polite">
            {isLoading ? 'Loading…' : ' '}
          </div>
        </div>

        {columnFiltersLimited && items.length > 0 && (
          <p className="table-column-filter-hint">
            Column filter counts are based on the first {items.length} of {remoteTotal} logs. Narrow search or panel filters to refine.
          </p>
        )}

        <div className="table-wrap">
          <table className="permissions-table logs-table">
            <thead>
              <tr>
                {[
                  { key: 'time', label: 'Time' },
                  { key: 'actor', label: 'Actor' },
                  { key: 'action', label: 'Action' },
                  { key: 'entity', label: 'Entity' },
                  { key: 'entityId', label: 'EntityId' },
                ].map((column) => (
                  <th key={column.key}>
                    <div className="table-th-content">
                      <TableColumnFilter
                        columnKey={column.key}
                        label={column.label}
                        options={columnFilterOptions[column.key] || []}
                        selectedValues={columnFilters[column.key]}
                        onChange={onColumnFilterChange}
                        disabled={isLoading || items.length === 0}
                      />
                      <span className="logs-th-label">{column.label}</span>
                    </div>
                  </th>
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {!isLoading && items.length > 0 && pagedItems.length === 0 && (
                <tr>
                  <td colSpan={6} className="table-empty-filtered">
                    No logs match the selected column filters.
                  </td>
                </tr>
              )}
              {items.length === 0 && !isLoading ? (
                <tr>
                  <td colSpan={6} className="hint-text">
                    No logs found.
                  </td>
                </tr>
              ) : (
                pagedItems.map((row) => (
                  <tr key={row.id || row.Id}>
                    <td
                      data-label="Time"
                      title={formatUtcToLocal(row.dateTimeUtc || row.DateTimeUtc || row.dateTime || row.DateTime, { full: true })}
                    >
                      {formatUtcToLocal(row.dateTimeUtc || row.DateTimeUtc || row.dateTime || row.DateTime)}
                    </td>
                    <td data-label="Actor">{row.actorEmail || row.ActorEmail || '—'}</td>
                    <td data-label="Action">{row.action || row.Action}</td>
                    <td data-label="Entity">{row.entityType || row.EntityType}</td>
                    <td data-label="EntityId">{row.entityId || row.EntityId || '—'}</td>
                    <td className="platform-user-actions-cell">
                      <button
                        type="button"
                        className="ghost-button admin-inline-button compact"
                        onClick={() => setDetails(row)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="table-pager">
          <button type="button" className="ghost-button admin-inline-button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            Prev
          </button>
          <div className="table-pager-info">
            <span>
              Page {page} of {totalPages}
            </span>
            <small>
              {filteredCount} log{filteredCount === 1 ? '' : 's'}
              {columnFiltersLimited ? ` (of ${remoteTotal} total)` : ''}
            </small>
          </div>
          <RoundedSelect
            value={String(pageSize)}
            onChange={(val) => {
              const next = Number(val) || 50;
              setPageSize(next);
              setPage(1);
            }}
            options={[
              { value: '20', label: '20 / page' },
              { value: '50', label: '50 / page' },
              { value: '100', label: '100 / page' },
            ]}
          />
          <button type="button" className="ghost-button admin-inline-button" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
            Next
          </button>
        </div>
      </motion.article>

      <FloatingNotices error={error} info={info} onDismissError={() => setError('')} onDismissInfo={() => setInfo('')} />

      {typeof document !== 'undefined' &&
        createPortal(
          <AnimatePresence>
            {filtersOpen && (
              <motion.div
                className="logs-filters-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onMouseDown={(e) => {
                  if (e.target === e.currentTarget) {
                    setFiltersOpen(false);
                    setFiltersPos(null);
                  }
                }}
              >
                <motion.div
                  className="logs-filters-popover"
                  initial={{ opacity: 0, y: 10, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.98 }}
                  transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                  style={
                    filtersPos
                      ? { position: 'fixed', top: filtersPos.top, left: filtersPos.left }
                      : { position: 'fixed', top: 82, left: '50%', transform: 'translateX(-50%)' }
                  }
                >
                  <div className="logs-filters-header">
                    <strong>Filters</strong>
                    <button
                      type="button"
                      className="ghost-button admin-inline-button compact"
                      onClick={() => {
                        setFiltersOpen(false);
                        setFiltersPos(null);
                      }}
                      aria-label="Close filters"
                    >
                      <X size={14} />
                    </button>
                  </div>

                  <div className="profile-form">
                    <label>Action</label>
                    <RoundedSelect
                      value={action || 'All'}
                      onChange={(val) => {
                        setPage(1);
                        setAction(val === 'All' ? '' : val);
                      }}
                      searchable
                      searchPlaceholder="Find action..."
                      options={[{ value: 'All', label: 'All actions' }, ...actionOptions.map((a) => ({ value: a, label: a }))]}
                    />

                    <label>Entity type</label>
                    <RoundedSelect
                      value={entityType || 'All'}
                      onChange={(val) => {
                        setPage(1);
                        setEntityType(val === 'All' ? '' : val);
                      }}
                      searchable
                      searchPlaceholder="Find entity type..."
                      options={[{ value: 'All', label: 'All entities' }, ...entityTypeOptions.map((t) => ({ value: t, label: t }))]}
                    />

                    <label>Actor email (exact)</label>
                    <input
                      placeholder="user@company.com"
                      value={actorEmail}
                      onChange={(event) => {
                        setActorEmail(event.target.value);
                        setPage(1);
                      }}
                    />

                    <div className="logs-filters-dates">
                      <div>
                        <label>From</label>
                        <input
                          type="datetime-local"
                          value={fromUtc}
                          onChange={(e) => {
                            setFromUtc(e.target.value);
                            setPage(1);
                          }}
                        />
                      </div>
                      <div>
                        <label>To</label>
                        <input
                          type="datetime-local"
                          value={toUtc}
                          onChange={(e) => {
                            setToUtc(e.target.value);
                            setPage(1);
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="inline-row logs-filters-actions">
                    <button
                      type="button"
                      className="ghost-button admin-inline-button"
                      onClick={() => {
                        setAction('');
                        setEntityType('');
                        setActorEmail('');
                        setFromUtc('');
                        setToUtc('');
                        setPage(1);
                      }}
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      className="primary-button admin-inline-button"
                      onClick={() => {
                        setFiltersOpen(false);
                        setFiltersPos(null);
                      }}
                    >
                      Apply
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}

      {typeof document !== 'undefined' &&
        createPortal(
          <AnimatePresence>
            {details && (
              <motion.div className="modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <motion.div
                  className="modal-card"
                  initial={{ opacity: 0, y: 24, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 18, scale: 0.98 }}
                  transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                >
                  <h3>Log details</h3>
                  <p className="hint-text">
                    <strong>{details.action || details.Action}</strong> · {details.entityType || details.EntityType}{' '}
                    {details.entityId || details.EntityId ? `(${details.entityId || details.EntityId})` : ''}
                  </p>
                  <div className="profile-form">
                    <label>Time</label>
                    <input value={formatUtcToLocal(details.dateTimeUtc || details.DateTimeUtc || details.dateTime || details.DateTime, { full: true })} readOnly />
                    <label>Actor</label>
                    <input value={details.actorEmail || details.ActorEmail || ''} readOnly />
                    <label>Changes</label>
                    <textarea
                      value={tryPrettyJson(details.changes || details.Changes)}
                      readOnly
                      style={{ minHeight: 160, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}
                    />
                    <label>Context</label>
                    <textarea
                      value={tryPrettyJson(details.context || details.Context)}
                      readOnly
                      style={{ minHeight: 140, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}
                    />
                  </div>
                  <div className="inline-row">
                    <button type="button" className="ghost-button admin-inline-button" onClick={() => setDetails(null)}>
                      Close
                    </button>
                    <button
                      type="button"
                      className="primary-button admin-inline-button"
                      onClick={async () => {
                        const text = [
                          `Time: ${formatUtcToLocal(details.dateTimeUtc || details.DateTimeUtc || details.dateTime || details.DateTime)}`,
                          `Actor: ${details.actorEmail || details.ActorEmail || ''}`,
                          `Action: ${details.action || details.Action}`,
                          `EntityType: ${details.entityType || details.EntityType}`,
                          `EntityId: ${details.entityId || details.EntityId || ''}`,
                          '',
                          'Changes:',
                          tryPrettyJson(details.changes || details.Changes),
                          '',
                          'Context:',
                          tryPrettyJson(details.context || details.Context),
                        ].join('\n');
                        try {
                          await navigator.clipboard.writeText(text);
                          setInfo('Copied to clipboard.');
                        } catch {
                          setInfo('Copy failed.');
                        }
                      }}
                    >
                      Copy
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
    </section>
  );
}

